/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Operation } from "@apollo/client";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useRef } from "react";
import { captureException } from "@/utils/capture-exception";
import { getUrls } from "@/utils/get-urls";
import { keepLoadedPages } from "@/utils/keep-loaded-pages";
import { nameFailedMutations } from "@/utils/mutation-errors";
import { withNumberedPages } from "@/utils/numbered-pages";
import { pendingMutationsLink } from "@/utils/pending-mutations";
import { settleRefetches } from "@/utils/settled-refetches";
import {
  ApolloClient,
  ApolloProvider,
  from,
  InMemoryCache,
  NetworkStatus,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import { useAuth0 } from "@auth0/auth0-react";
import { Kind, OperationTypeNode } from "graphql";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { useLocalStorage } from "react-use";

// Operations that are slow (Google Analytics, long feeds) or return large tables, and the one that the rest of a
// recruitment's page waits for.
const OWN_BATCH_OPERATIONS = new Set([
  "MyRecruitmentUser",
  "AdViews",
  "RecruitmentTrends",
  "ViewsStatistics",
  "OverviewStatistics",
  "DevicesStatistics",
  "CalendarEvents",
  "ActivityFeed",
  "Application",
  "Applications",
  "AllApplications",
  "PaginatedApplications",
  "PaginatedCompanyApplicants",
  "GeneralSearchDocuments",
]);

interface ApolloProviderValue {
  activeCompanyId: string | undefined;
  setActiveCompanyId: (id: string | undefined) => void;
}

const ApolloProviderContext = createContext<ApolloProviderValue>({
  activeCompanyId: undefined,
  setActiveCompanyId: () => null,
});

function isMutationOperation(operation: Operation) {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.MUTATION
  );
}

export function ApolloProviderWithAuth0({ children }: PropsWithChildren) {
  const [activeCompanyId, setActiveCompanyId] = useLocalStorage<string>(
    "activeCompanyId",
    undefined,
  );
  const client = useRef<ApolloClient<any> | null>(null);
  const history = useHistory();
  const { getAccessTokenSilently, logout } = useAuth0();

  if (!client.current) {
    const httpLink = new BatchHttpLink({
      uri: getUrls().apiUrl,
      batchMax: 5,
      // A page's queries start in one commit, so this only has to span that. Every request waits this long.
      batchInterval: 10,
      fetch,
      // A batch answers when its slowest operation is done, so slow or large ones travel on their own. The key still
      // includes the headers, as the default key does, so callers with different headers never share a batch.
      batchKey: (operation) => {
        const { http, fetchOptions, credentials, headers } =
          operation.getContext() as Record<string, unknown>;
        const lane = OWN_BATCH_OPERATIONS.has(operation.operationName)
          ? operation.operationName
          : "";
        return (
          lane +
          JSON.stringify({ http, options: fetchOptions, credentials, headers })
        );
      },
    });

    // const httpLink = new HttpLink({
    //   uri: getUrls().apiUrl + "/graphql",
    //   fetch,
    // });

    const errorLink = onError((error) => {
      const { graphQLErrors, operation } = error;
      const isMutation = isMutationOperation(operation);
      const redirectOnForbidden =
        operation.getContext().redirectOnForbidden === true;
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message }) => {
          if (
            message === "You are not allowed to change data on another account!"
          ) {
            toast.error("Du får inte ändra på data på ett annat konto");
          }

          if (
            message === "missing_write_privileges" ||
            (message === "missing_read_privileges" && isMutation)
          ) {
            toast.error(
              "Du saknar behörighet att ändra den här typen av data.",
            );
          }

          if (
            message === "missing_read_privileges" &&
            !isMutation &&
            redirectOnForbidden
          ) {
            history.replace("/not-authorized");
          }

          // Every request fails until the id is gone, and the page shows the
          // person it was viewing as, so it starts over rather than carry on.
          if (message === "view_as_refused") {
            localStorage.removeItem("viewAsUserId");
            sessionStorage.setItem("viewAsRefused", "true");
            history.replace("/");
            window.location.reload();
          }

          if (
            message === "not_logged_in" ||
            message === "User is not authenticated" ||
            message === "You are not an authorized user!"
          ) {
            if (!history.location.pathname.includes("/login")) {
              localStorage.removeItem("viewAsUserId");
              void logout({
                logoutParams: {
                  returnTo: getUrls().redirectUrl,
                },
              });
            }
          }
        });
      }
    });

    const authLink = setContext(async (_, { headers }) => {
      const viewAsUserId = JSON.parse(
        localStorage.getItem("viewAsUserId") || '""',
      );
      const activeCompanyId = JSON.parse(
        localStorage.getItem("activeCompanyId") || '""',
      );
      let devPersona = false;
      if (import.meta.env.DEV) {
        try {
          devPersona =
            JSON.parse(localStorage.getItem("devPersonaEnabled") || "false") ===
            true;
        } catch {
          devPersona = false;
        }
      }

      let token: string | null = null;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://server.cruitive.com",
            scope: "openid email profile",
          },
        });
      } catch (e) {
        console.error(e);
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        headers: {
          ...headers,
          view_as_user_id: viewAsUserId,
          active_company_id: activeCompanyId,
          ...(devPersona ? { dev_persona: "1" } : {}),
          authorization: token ? `Bearer ${token}` : "",
          // Accept: 'multipart/mixed;deferSpec=20220824, application/json',
        },
      };
    });

    // Queries keep RetryLink's defaults, but a mutation is never retried: if
    // only the response was lost, the server may already have run it, and a
    // second run could send an applicant the same email or SMS twice.
    const retryLink = new RetryLink({
      attempts: {
        retryIf: (error, operation) =>
          !!error && !isMutationOperation(operation),
      },
    });

    const keyArgs = (args: Record<string, any> | null) => {
      return Object.keys(args || {}).filter(
        (key) => key !== "after" && key !== "limit",
      );
    };
    // Load-more lists, plus the numbered pages ConnectedTable asks for.
    const connection = () => withNumberedPages(relayStylePagination(keyArgs));

    const cache = new InMemoryCache({
      typePolicies: {
        // The same option comes back under the same id with a count that depends on
        // what it was asked with (the filters, a recruitment, a company), so each
        // answer has to keep its own.
        FilterItem: { keyFields: false },
        CompetenceAnswerTypeFilterData: { keyFields: false },
        CompetenceFilterData: { keyFields: false },
        CompetenceTypeFilterData: { keyFields: false },
        ConsultantProjectActiveFilterData: { keyFields: false },
        EducationFilterData: { keyFields: false },
        ProfessionFieldFilterData: { keyFields: false },
        RecruitmentFilterData: { keyFields: false },
        TagFilterData: { keyFields: false },
        TalentProfileFilterData: { keyFields: false },
        Query: {
          fields: {
            // MeQuery loads the caller's company in full, so `company(id)` reads it from the cache instead of waiting
            // for another round trip. A company that isn't cached in full is still fetched.
            company: {
              read(_, { args, toReference }) {
                return typeof args?.id === "string"
                  ? toReference({ __typename: "Company", id: args.id })
                  : undefined;
              },
            },
            activityFeed: connection(),
            adListWidgetRecruitments: connection(),
            adminApplications: connection(),
            allMatchingCriterias: connection(),
            applicantMessages: withNumberedPages(
              keepLoadedPages(relayStylePagination(keyArgs)),
            ),
            assignmentBoards: connection(),
            applicantsAdmin: connection(),
            applications: connection(),
            applicationScreeningFormParticipations: connection(),
            availableMatchingEvents: connection(),
            calendarEventsList: connection(),
            childsEventMatches: connection(),
            childCompanysMatchingEvents: connection(),
            childCompanysMatchingEventParticipants: connection(),
            comments: connection(),
            companyApplicants: connection(),
            companyApplicantsSimple: connection(),
            companiesAdmin: connection(),
            companyApplicantApplications: connection(),
            companyCompetences: connection(),
            companyUsers: connection(),
            consultants: connection(),
            consultantCustomers: connection(),
            consultantProjectCosts: connection(),
            consultantProjects: connection(),
            contactPersons: connection(),
            contracts: connection(),
            documents: connection(),
            errors: connection(),
            eventTriggers: connection(),
            forms: connection(),
            groupCompanies: connection(),
            groupCompanyApplicants: connection(),
            matchingEventProfiles: connection(),
            groupCompanyCompanies: connection(),
            hostEventMatches: connection(),
            hostedMatchingEvents: connection(),
            identifiedNeeds: connection(),
            interviews: connection(),
            invitedUsers: connection(),
            interviewGuideTemplates: connection(),
            matchingEventCompanyInvitations: connection(),
            matchingEventCompanyRequests: connection(),
            matchingEventParticipants: connection(),
            matchingEventTalentProfiles: connection(),
            messageTemplates: connection(),
            messageTemplatesCustom: connection(),
            notes: connection(),
            notifications: connection(),
            pageTemplates: connection(),
            pageVersions: connection(),
            participantsEventMatches: connection(),
            recruitmentApplicantMessages: connection(),
            recruitmentsAdmin: connection(),
            recruitmentsPage: connection(),
            recruitmentsSimple: connection(),
            recruitmentsPageAdmin: connection(),
            recruitmentTemplates: connection(),
            referenceForms: connection(),
            screeningForms: connection(),
            talentProfilesOfEvent: connection(),
            applicantsApplications: connection(),
            hostsEventMatches: connection(),
            interviewBookingGroupApplications: connection(),
            applicationsCompaniesFilter: connection(),
            applicationsRecruitmentsFilter: connection(),
            applicationStatusesFilter: connection(),
            templatePageBlocks: connection(),
            adminCompaniesCursorBased: connection(),
            baseVariablesCursorBased: connection(),
            careerPagePages: connection(),
            cursorBasedGroupCompanyApplicants: connection(),
            departments: connection(),
            fileTemplates: connection(),
            identifiedNeedForms: connection(),
            interviewBookingGroups: connection(),
            interviewBookingGroupUninvitedApplications: connection(),
            recruitmentsAvailableOnInterviewBookingGroup: connection(),
            recruitmentTeams: connection(),
            talentProfilesOfParticipant: connection(),
            userRolesCursorBased: connection(),
          },
        },
      },
    });

    // function createNestedObject<T>(path: (string | number)[], data: T): object {
    //   const result = {}

    //   set(result, path, data)

    //   // Fill previous array elements with empty objects
    //   if (typeof last(path) === 'number') {
    //     const arrayPath = path.slice(0, -1)
    //     const arrayLength = last(path) as number

    //     set(
    //       result,
    //       arrayPath,
    //       times(arrayLength, (index) => (index === arrayLength - 1 ? data : {})),
    //     )
    //   }

    //   return result
    // }

    // const deferredMergeLink = new ApolloLink((operation, forward) => {
    //   return forward(operation).map((response) => {
    //     if (!('path' in response) || !Array.isArray(response.path)) {
    //       return response
    //     }

    //     const data = response.data as Record<string, unknown>
    //     const path = response.path as (string | number)[]

    //     const nestedData = createNestedObject(path, data)

    //     return { ...response, data: nestedData }
    //   })
    // })

    // setInterval(async () => {
    //   const data = await client.current?.query({
    //     query: gql`
    //       query {
    //         companyIds
    //       }
    //     `,
    //     fetchPolicy: "network-only",
    //   });

    // }, 100);

    const link = from([
      pendingMutationsLink,
      retryLink,
      authLink,
      errorLink,
      httpLink,
    ]);

    client.current = new ApolloClient({
      link,
      cache,
      name: "cruitive-app",
      version: "2.1.4",
      defaultOptions: {
        // Unread counts, the messages table and the assignment feed poll every 10 to 30 s. A tab nobody is looking at
        // skips those polls and catches up as soon as it is shown again (see below).
        watchQuery: { skipPollAttempt: () => document.hidden },
      },
    });
    settleRefetches(client.current, (error, queryName) => {
      captureException(error, {
        level: "warning",
        fingerprint: ["failed-refetch", queryName ?? "unknown"],
        tags: { refetched_query: queryName ?? "unknown" },
      });
    });
    nameFailedMutations(client.current);
  }

  // The next poll can be 30 s away when a hidden tab comes back, and the badges would be out of date until then.
  useEffect(() => {
    const catchUpOnPolls = () => {
      if (document.hidden) return;
      client.current?.getObservableQueries("active").forEach((query) => {
        if (query.options.pollInterval) {
          // What a poll tick runs. refetch() would replace a paged list in the cache rather than merge into it.
          query
            .reobserve({ fetchPolicy: "network-only" }, NetworkStatus.poll)
            .catch(() => undefined);
        }
      });
    };
    document.addEventListener("visibilitychange", catchUpOnPolls);
    return () =>
      document.removeEventListener("visibilitychange", catchUpOnPolls);
  }, []);

  // The id is shared between tabs and read for every request, so when another tab
  // starts or ends a view-as, this one would go on showing the old person while
  // acting as the new one. Start over as whoever the id says now.
  useEffect(() => {
    const reloadOnViewAsChange = (event: StorageEvent) => {
      if (event.key === "viewAsUserId") {
        window.location.reload();
      }
    };
    window.addEventListener("storage", reloadOnViewAsChange);
    return () => window.removeEventListener("storage", reloadOnViewAsChange);
  }, []);

  return (
    <ApolloProviderContext.Provider
      value={{ activeCompanyId, setActiveCompanyId }}
    >
      <ApolloProvider client={client.current}>{children}</ApolloProvider>
    </ApolloProviderContext.Provider>
  );
}

export const useApolloWithAuth0 = () => useContext(ApolloProviderContext);
